import React from 'react';
import { motion } from 'framer-motion';

/**
 * Badge Component - Badge eleganti con animazioni e icone
 * 
 * @param {Object} props
 * @param {string} props.variant - Variante colore: 'success', 'warning', 'danger', 'info', 'purple', 'gray'
 * @param {React.ReactNode} props.children - Contenuto del badge
 * @param {React.ReactNode} props.icon - Icona opzionale
 * @param {boolean} props.pulse - Attiva animazione pulsante
 * @param {boolean} props.animate - Attiva animazione di entrata
 * @param {string} props.className - Classi CSS aggiuntive
 */
const Badge = ({ 
    variant = 'gray', 
    children, 
    icon, 
    pulse = false,
    animate = false,
    className = '' 
}) => {
    const variants = {
        success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        warning: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
        danger: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
        gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };

    const BadgeContent = () => (
        <span className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
            transition-all duration-200
            ${variants[variant] || variants.gray}
            ${pulse ? 'animate-pulse' : ''}
            ${className}
        `}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children}
        </span>
    );

    if (animate) {
        return (
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                className="inline-block"
            >
                <BadgeContent />
            </motion.div>
        );
    }

    return <BadgeContent />;
};

/**
 * StatusBadge - Badge con indicatore di stato pulsante
 */
export const StatusBadge = ({ 
    status = 'offline', 
    label, 
    animate = true 
}) => {
    const statusColors = {
        online: 'bg-green-500',
        offline: 'bg-gray-400',
        busy: 'bg-red-500',
        away: 'bg-yellow-500',
    };

    const StatusContent = () => (
        <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-700">
            <span className="relative flex h-3 w-3">
                {status === 'online' && (
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                )}
                <span className={`relative inline-flex rounded-full h-3 w-3 ${statusColors[status]}`}></span>
            </span>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {label || status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        </span>
    );

    if (animate) {
        return (
            <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="inline-block"
            >
                <StatusContent />
            </motion.div>
        );
    }

    return <StatusContent />;
};

/**
 * CountBadge - Badge con contatore (es. notifiche)
 */
export const CountBadge = ({ count = 0, max = 99, pulse = false }) => {
    const displayCount = count > max ? `${max}+` : count;
    
    if (count === 0) return null;

    return (
        <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`
                absolute -top-1 -right-1 
                bg-red-500 text-white text-xs font-bold
                rounded-full w-5 h-5 
                flex items-center justify-center
                ${pulse ? 'animate-bounce' : ''}
            `}
        >
            {displayCount}
        </motion.span>
    );
};

export default Badge;

